import {
  pgTable,
  text,
  timestamp,
  uuid,
  boolean,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export const consentRecords = pgTable(
  "consent_records",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(),
    consentType: text("consent_type").notNull(),
    consentMethod: text("consent_method").notNull(),
    lawfulBasis: text("lawful_basis").notNull(),
    consentTextVersionId: text("consent_text_version_id"),
    initialSignupAt: timestamp("initial_signup_at", tz).notNull(),
    initialSignupIpHash: text("initial_signup_ip_hash"), // HMAC-SHA-256 (IP_HASH_SECRET) — renamed from initial_signup_ip
    confirmationSentAt: timestamp("confirmation_sent_at", tz),
    confirmationClickedAt: timestamp("confirmation_clicked_at", tz),
    confirmationClickedIpHash: text("confirmation_clicked_ip_hash"), // HMAC-SHA-256 (IP_HASH_SECRET) — renamed from confirmation_clicked_ip
    revokedAt: timestamp("revoked_at", tz),
    revocationMethod: text("revocation_method"),
  },
  (table) => [
    index("idx_consent_records_tenant_contact").on(
      table.tenantId,
      table.personId,
    ),
    index("idx_consent_records_tenant_type").on(
      table.tenantId,
      table.consentType,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Channel Preferences — per-contact opt-in/out per channel (F7)
// ---------------------------------------------------------------------------
// Row-per-channel model: one row per (tenant, person, channel).
// Council overruled booleans on consentRecords — separate table avoids
// schema bloat and supports future channel additions without ALTER TABLE.
// updatedAt tracks when the preference last changed (audit trail).

export const PREFERENCE_CHANNELS = [
  "email",
  "sms",
  "whatsapp",
  "push",
  "in_app",
] as const;
export type PreferenceChannel = (typeof PREFERENCE_CHANNELS)[number];

export const channelPreferences = pgTable(
  "channel_preferences",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(),
    channel: text("channel").notNull(), // PreferenceChannel
    isOptedIn: boolean("is_opted_in").notNull().default(true),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
    updatedBy: text("updated_by"), // actor who changed the preference (user_id or "system")
  },
  (table) => [
    uniqueIndex("uq_channel_pref_tenant_person_channel").on(
      table.tenantId,
      table.personId,
      table.channel,
    ),
    index("idx_channel_pref_tenant_person").on(table.tenantId, table.personId),
  ],
);

// ---------------------------------------------------------------------------
// Audit Log
// ---------------------------------------------------------------------------

export const auditLog = pgTable(
  "audit_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    actorId: text("actor_id"),
    actorType: text("actor_type").notNull(),
    action: text("action").notNull(),
    resourceType: text("resource_type").notNull(),
    resourceId: text("resource_id"),
    details: jsonb("details").$type<Record<string, unknown>>(),
    ipAddressHash: text("ip_address_hash"), // HMAC-SHA-256 (IP_HASH_SECRET) — never raw IP (GDPR). Column renamed from ip_address.
    userAgent: text("user_agent"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_audit_log_tenant").on(table.tenantId),
    index("idx_audit_log_tenant_action").on(table.tenantId, table.action),
    index("idx_audit_log_tenant_resource").on(
      table.tenantId,
      table.resourceType,
      table.resourceId,
    ),
    index("idx_audit_log_tenant_created").on(table.tenantId, table.createdAt),
  ],
);
